let uniqueName = 0;

// for tests only
// eslint-disable-next-line no-underscore-dangle
export const _resetUniqueName = (value = 0) => { uniqueName = value; };

// todo this is a temporary polyfill while we figure out ES2020 in Glu
export const fromEntries = entries => entries.reduce(
  (memo, [key, value]) => ({ ...memo, [key]: value }),
  {}
);


// compareTo function for [name, index] that sorts by index
export const sortByIndex = (a, b) => (a[1] < b[1] ? -1 : 1);

/**
 * Converts an array of initial data into a map of unique keys to indices
 * @param {string} name - The name to use as the root for the unique keys
 * @param {array} initialData - An array of initial entries
 * @returns {Object<string, number>} The resultant map
 */
export function createNameToIndexMap(name, initialData) {
  return initialData.reduce((memo, value, index) => ({
    ...memo,
    [`${name}-${uniqueName++}`]: index // eslint-disable-line no-plusplus
  }), {});
}

/**
 * Updates a nameToIndexMap based on external changes to the data
 * @param {string} name - The name to use as the root for the unique keys
 * @param {Object<string, number>} prevMap - The previous iteration of the map
 * @param {array} data - The updated data
 * @returns {Object<string, number>} The resultant map
 */
export function patchNameToIndexMap(name, prevMap, data) {
  const maxIndex = data.length - 1;
  const prevMapValues = Object.values(prevMap);
  const prevMaxIndex = prevMapValues.length ? Math.max(...Object.values(prevMap)) : -1;

  if (prevMaxIndex === maxIndex) {
    return prevMap;
  }

  const sortedEntries = Object.entries(prevMap).sort(sortByIndex);

  return fromEntries([...new Array(data.length)].map((_, index) => [
    sortedEntries[index]?.[0] || `${name}-${uniqueName++}`, // eslint-disable-line no-plusplus
    index
  ]));
}

/**
 * Maps the array-based external representation of the list data to the internal
 * object structure with unique keys
 * @param {object<string, number>} nameToIndexMap - Mapping of names to indices
 * @param {array} dataArray - The external data array
 * @returns {Object} An object of the same data with unique keys
 */
export function mapPublicDataToPrivate(nameToIndexMap, dataArray) {
  const nameArray = Object.entries(nameToIndexMap).reduce((memo, [name, index]) => {
    memo[index] = name; // eslint-disable-line no-param-reassign

    return memo;
  }, []);

  return dataArray.reduce((privateMemo, value, index) => ({
    ...privateMemo,
    [nameArray[index]]: value
  }), {});
}

/**
 * The reverse of the above function.
 * Maps the internal object structure of the data to the public array format
 * @param {object<string, number>} nameToIndexMap - Mapping of names to indices
 * @param {Object} dataObject - The internally mapped data object
 * @returns {array} The data array as expected externally
 */
export function mapPrivateDataToPublic(nameToIndexMap, dataObject) {
  return Object.entries(nameToIndexMap).reduce((publicArray, [name, index]) => {
    publicArray[index] = dataObject[name]; // eslint-disable-line no-param-reassign

    return publicArray;
  }, []);
}
